/**
 * SDK 登录协调层(无 zustand,基于 useSyncExternalStore)
 *
 * 提供 SDK 级通用登录能力:openLogin(onSuccess?) 打开登录弹窗 + 登记续接动作,
 * 登录成功后 onLoginSuccess 写 user-storage(匹配 zustand persist)+ 触发续接。
 * 续接机制通用:本次领红包 openLogin(() => doClaim());未来「点击立即预定弹登录」
 * 用 openLogin(() => 跳提单) 接口预留(本次不做)。
 *
 * 登录态闭环:writeUserStorage 写 LS -> buildContextHeaders(readUserToken)读到 token ->
 * 后续业务请求自动带 Authorization。主流程 user-store 的 storage 监听据此同步(跨 tab)。
 *
 * useSyncExternalStore 模式参考 src/ui/hooks/useMediaQuery.ts(模块级 store + listeners)。
 */
import { useSyncExternalStore } from 'react';
import type { LoginResponse, User } from '@/types/auth';
import { readIsLoggedIn, writeUserStorage, USER_STORAGE_KEY } from '@/lib/shared-storage';

interface LoginManagerState {
  /** 登录弹窗是否打开 */
  loginOpen: boolean;
  /** 登录成功后续接动作(领红包/预定等);关闭弹窗则丢弃 */
  onSuccess: (() => void) | null;
  /** 登录态镜像(响应 LS 变化) */
  isLoggedIn: boolean;
}

let state: LoginManagerState = {
  loginOpen: false,
  onSuccess: null,
  isLoggedIn: typeof window !== 'undefined' ? readIsLoggedIn() : false,
};

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      // 单个 listener 异常不影响其他
    }
  });
}

function setState(next: Partial<LoginManagerState>): void {
  state = { ...state, ...next };
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 打开登录弹窗;传 onSuccess 则登记续接(覆盖旧值),不传则保留已有续接 */
export function openLogin(onSuccess?: () => void): void {
  setState({ loginOpen: true, onSuccess: onSuccess ?? state.onSuccess });
}

/** 关闭登录弹窗,丢弃续接(用户主动关闭不应重放任何动作) */
export function closeLogin(): void {
  setState({ loginOpen: false, onSuccess: null });
}

/**
 * 登录成功:先写 LS(让 buildContextHeaders 读到 token)再触发续接。
 * 写 LS 触发 storage 事件,主流程 user-store 据此 syncFromStorage(跨 tab 同步)。
 */
export function onLoginSuccess(response: LoginResponse): void {
  const user: User = {
    userID: response.userID,
    nickName: response.nickName,
    avatar: response.avatar,
    mobile: response.mobile,
    email: response.email,
    isNewRegist: response.isNewRegist,
    showPartner: response.showPartner,
  };
  writeUserStorage({
    user,
    token: response.accessToken.access_token,
    isLoggedIn: true,
  });
  const action = state.onSuccess;
  setState({ loginOpen: false, onSuccess: null, isLoggedIn: true });
  if (action) action();
}

/** 订阅登录弹窗开关(SSR 首帧 false,hydration 后真实值) */
export function useLoginOpen(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => state.loginOpen,
    () => false
  );
}

/** 订阅登录态(SSR 首帧 false,hydration 后 readIsLoggedIn) */
export function useIsLoggedIn(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => state.isLoggedIn,
    () => false
  );
}

// 监听 storage 事件:主流程登录/登出或其他 tab 改动 user-storage 时同步 isLoggedIn
// (同 tab 的 onLoginSuccess 已直接 setState,storage 事件只在其他 tab 触发,无死循环)
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === USER_STORAGE_KEY) {
      setState({ isLoggedIn: readIsLoggedIn() });
    }
  });
}
